import { keepPreviousData, useQueries, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { tmsApi, type OrderEmissions } from '../client';

export const orderEmissionsKeys = {
  all: ['tms', 'orderEmissions'] as const,
  byOrder: (orderId: string, inputsKey: string) =>
    ['tms', 'orderEmissions', 'order', orderId, inputsKey] as const,
  byOrders: (organisationId: string, orderIds: string[]) =>
    ['tms', 'orderEmissions', 'orders', organisationId, ...orderIds] as const,
};

/**
 * The order's stored CO2 estimate. The server decides whether it needs
 * replanning; `inputsKey` is anything the screen already knows changes with
 * the journey (when the order, its tasks and bookings were fetched), so an
 * edit asks again without every mutation having to know about this cache.
 * The previous figure stays on screen while the new one loads.
 */
export function useOrderEmissions(orderId: string | null, inputsKey: string) {
  return useQuery<OrderEmissions | null, Error>({
    queryKey: orderId ? orderEmissionsKeys.byOrder(orderId, inputsKey) : orderEmissionsKeys.all,
    queryFn: () => {
      if (!orderId) throw new Error('No order id');
      return tmsApi.getOrderEmissions(orderId);
    },
    enabled: !!orderId,
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

/** The server's per-call cap on order ids. */
const CHUNK = 100;

/**
 * Stored CO2 estimates for a list of orders (the Plan cockpit), keyed by
 * order id. The server replans a few per call; while it reports orders still
 * pending, each chunk asks again every few seconds until they are all in.
 */
export function useOrdersEmissions(organisationId: string | null, orderIds: string[]) {
  const chunks = useMemo(() => {
    const ids = [...new Set(orderIds)].sort();
    const out: string[][] = [];
    for (let i = 0; i < ids.length; i += CHUNK) out.push(ids.slice(i, i + CHUNK));
    return out;
  }, [orderIds]);

  const queries = useQueries({
    queries: chunks.map((ids) => ({
      queryKey: organisationId
        ? orderEmissionsKeys.byOrders(organisationId, ids)
        : orderEmissionsKeys.all,
      queryFn: () => {
        if (!organisationId) throw new Error('No organisation id');
        return tmsApi.getOrdersEmissions(organisationId, ids);
      },
      enabled: !!organisationId,
      staleTime: 60 * 1000,
      retry: false,
      refetchInterval: (q: { state: { data?: { pendingOrderIds: string[] } } }) =>
        (q.state.data?.pendingOrderIds.length ?? 0) > 0 ? 3000 : false,
    })),
  });

  const signature = queries.map((q) => q.dataUpdatedAt).join(',');
  return useMemo(() => {
    const byOrderId = new Map<string, OrderEmissions>();
    for (const q of queries) for (const e of q.data?.estimates ?? []) byOrderId.set(e.orderId, e);
    return byOrderId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);
}
